/**
 * Tabs: an accessible strip of triggers with keyboard navigation, whose
 * selection is the caller's signal and whose panel shows only the selected tab.
 */

import { isHtmlElement } from '@crown/framework/dom';
import type {
  Child,
  Instruction,
  Reader,
  SignalLike,
} from '@crown/framework/ir';
import type { Signal } from '@crown/signal';
import blockTemplate from './tabs-block.tsrx';
import template from './tabs.tsrx';

import './tabs.css';

export type TabsOrientation = 'horizontal' | 'vertical';

/** One trigger in the strip, as the template reads it. */
export interface TabTriggerView {
  readonly id: string;
  readonly label: string;
  readonly badge?: string;
  readonly disabled?: boolean;
}

export interface TabsTemplateProps {
  /** The strip's accessible name, when the page has no heading that names it. */
  readonly label?: string;
  readonly triggers: Reader<readonly TabTriggerView[]>;
  readonly active: SignalLike<string | null>;
  readonly onActivate: (tabId: string) => void;
  readonly onKeyDown: (event: KeyboardEvent, tabId: string) => void;
}

export interface TabsBlockTemplateProps {
  readonly classes: string;
  /** The strip of triggers. */
  readonly strip: Instruction;
  readonly panelId: Reader<string>;
  readonly labelledBy: Reader<string>;
  /** The selected tab's content. */
  readonly panel: Reader<Child>;
}

const TabsTemplate: (props: TabsTemplateProps) => Instruction = template;
const TabsBlockTemplate: (props: TabsBlockTemplateProps) => Instruction =
  blockTemplate;

/**
 * The trigger an arrow, Home or End key moves to from `currentTabId`, skipping
 * none and wrapping at the ends; null for any other key or a disabled target.
 */
function stepTab(
  event: KeyboardEvent,
  list: Element | null,
  currentTabId: string,
  orientation: TabsOrientation,
): HTMLElement | null {
  const triggers = Array.from(
    list?.querySelectorAll('[role="tab"]') ?? [],
  ).filter(isHtmlElement);
  const at = triggers.findIndex(
    (trigger) => trigger.dataset.tabId === currentTabId,
  );
  const previousKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
  const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
  let to: number;
  switch (event.key) {
    case previousKey:
      to = at > 0 ? at - 1 : triggers.length - 1;
      break;
    case nextKey:
      to = at < triggers.length - 1 ? at + 1 : 0;
      break;
    case 'Home':
      to = 0;
      break;
    case 'End':
      to = triggers.length - 1;
      break;
    default:
      return null;
  }
  event.preventDefault();
  const target = triggers[to];
  return target && !target.hasAttribute('disabled') ? target : null;
}

export interface TabsProps {
  /** The triggers, in order; read again when they change. */
  readonly tabs: Reader<readonly TabTriggerView[]>;
  /** The selected tab's id, which the strip both reads and writes. */
  readonly active: Signal.State<string>;
  /** The content of the selected tab only, so a tab that fetches does so when it opens. */
  readonly panel: (tabId: string) => Child;
  readonly label?: string;
  readonly orientation?: TabsOrientation;
  readonly onChange?: (tabId: string) => void;
}

/**
 * Tabs as instructions: an accessible strip, with its selection held in the
 * caller's signal and only the selected panel rendered.
 */
export function Tabs(props: TabsProps): Instruction {
  const orientation = props.orientation ?? 'horizontal';
  const activate = (tabId: string): void => {
    if (props.tabs().find((tab) => tab.id === tabId)?.disabled) return;
    if (props.active.get() === tabId) return;
    props.active.set(tabId);
    props.onChange?.(tabId);
  };
  return TabsBlockTemplate({
    classes:
      orientation === 'vertical'
        ? 'crown-tabs crown-tabs-vertical'
        : 'crown-tabs',
    strip: TabsTemplate({
      label: props.label,
      triggers: props.tabs,
      active: props.active,
      onActivate: activate,
      onKeyDown: (event, tabId) => {
        if (
          !(event instanceof KeyboardEvent) ||
          !isHtmlElement(event.currentTarget)
        )
          return;
        const next = stepTab(
          event,
          event.currentTarget.closest('[role="tablist"]'),
          tabId,
          orientation,
        );
        const nextTabId = next?.dataset.tabId;
        if (!next || !nextTabId) return;
        activate(nextTabId);
        next.focus();
      },
    }),
    panelId: () => `tab-panel-${props.active.get()}`,
    labelledBy: () => `tab-trigger-${props.active.get()}`,
    panel: () => props.panel(props.active.get()),
  });
}
